import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, object = logged in, false = anonymous
  const queryClient = useQueryClient();

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const onDenied = (e) => {
      queryClient.clear();
      setUser(false);
      toast.error(e.detail || "Masa aktif akun Anda telah berakhir.", { id: "access-denied" });
    };
    window.addEventListener("kb:access-denied", onDenied);
    return () => window.removeEventListener("kb:access-denied", onDenied);
  }, [queryClient]);

  const login = async (email, password) => {
    // Drop any cached data from a previous user so the new user never sees stale data.
    queryClient.clear();
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    // Clear all cached queries (dashboard, reports, materials, etc.) on user switch.
    queryClient.clear();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, checkAuth, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
